// Post-processing (docs/04 §8) in a single full-screen pass: the scene renders once into a
// multisampled HDR target; one shader then draws the ink outlines (from depth), the warm vignette
// grade, ACES tone mapping and sRGB output. One pass instead of a composer chain keeps it cheap on
// integrated GPUs at high pixel ratios. No bloom: the art direction keeps glow restrained.
import * as THREE from 'three';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';
import { isPhone } from '../app/pixels';

export type Quality = 'low' | 'medium' | 'high';

const FinalShader = {
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform sampler2D tDepth;
    uniform vec2 texel;
    uniform float cameraNear;
    uniform float cameraFar;
    uniform vec3 ink;
    uniform float strength;
    uniform float width;
    uniform float vignette;
    uniform float warmth;
    uniform float sharpen;
    varying vec2 vUv;

    // View-space z from depth. For a perspective projection it depends on depth alone (three.js's
    // perspectiveDepthToViewZ): the same value as unprojecting through the inverse projection.
    float depthToViewZ(float d) { return (cameraNear * cameraFar) / ((cameraFar - cameraNear) * d - cameraFar); }
    float viewZ(vec2 uv) { return depthToViewZ(texture2D(tDepth, uv).x); }

    void main() {
      vec4 c = texture2D(tDiffuse, vUv);
      // Sharpen: a contrast-adaptive unsharp mask (four taps). Restores the crispness bilinear
      // upscaling and texture filtering take away, without haloing already-sharp edges.
      if (sharpen > 0.0) {
        vec3 n = texture2D(tDiffuse, vUv + vec2(0.0, texel.y)).rgb;
        vec3 s = texture2D(tDiffuse, vUv - vec2(0.0, texel.y)).rgb;
        vec3 e = texture2D(tDiffuse, vUv + vec2(texel.x, 0.0)).rgb;
        vec3 w = texture2D(tDiffuse, vUv - vec2(texel.x, 0.0)).rgb;
        vec3 mn = min(c.rgb, min(min(n, s), min(e, w)));
        vec3 mx = max(c.rgb, max(max(n, s), max(e, w)));
        vec3 amp = clamp(min(mn, 2.0 - mx) / max(mx, 1e-4), 0.0, 1.0);
        vec3 k = sqrt(amp) * sharpen * 0.2;
        c.rgb = max((c.rgb - k * (n + s + e + w)) / (1.0 - 4.0 * k), 0.0);
      }
      // Ink: the second derivative of view depth is ~0 on any plane and spikes at silhouettes and
      // creases alike — five taps, no extra scene pass.
      float dc = texture2D(tDepth, vUv).x;
      if (strength > 0.0 && dc < 0.99999) {
        vec2 o = texel * width;
        float zc = depthToViewZ(dc);
        float lap = (abs(viewZ(vUv - vec2(o.x, 0.0)) + viewZ(vUv + vec2(o.x, 0.0)) - 2.0 * zc) +
                     abs(viewZ(vUv - vec2(0.0, o.y)) + viewZ(vUv + vec2(0.0, o.y)) - 2.0 * zc)) / max(abs(zc), 1.0);
        float fade = 1.0 - smoothstep(90.0, 160.0, -zc);
        float e = smoothstep(0.0012, 0.006, lap) * strength * fade;
        c.rgb = mix(c.rgb, ink * min(c.rgb * 1.5 + 0.08, vec3(1.0)), e);
      }
      // Grade: warm vignette.
      vec2 d = vUv - 0.5;
      c.rgb *= mix(1.0 - vignette, 1.0, smoothstep(0.85, 0.25, length(d * vec2(1.15, 1.0))));
      c.rgb += vec3(warmth, warmth * 0.45, -warmth * 0.3) * c.rgb;
      gl_FragColor = c;
      #include <tonemapping_fragment>
      #include <colorspace_fragment>
    }`,
};

export class PostFX {
  private target: THREE.WebGLRenderTarget;
  private quad: FullScreenQuad;
  private material: THREE.ShaderMaterial;
  /** Internal render scale (dynamic resolution). The canvas stays at full pixel ratio, so UI-space
   *  work (labels) stays sharp and the final pass upsamples + sharpens the scene. */
  private scale = 1;
  private sharpenBase: number;

  constructor(
    private renderer: THREE.WebGLRenderer,
    private scene: THREE.Scene,
    private camera: THREE.PerspectiveCamera,
    quality: Quality,
  ) {
    const size = renderer.getSize(new THREE.Vector2());
    const pr = renderer.getPixelRatio();
    const w = Math.max(1, Math.round(size.x * pr));
    const h = Math.max(1, Math.round(size.y * pr));
    this.sharpenBase = quality === 'low' ? 0 : 0.55;
    this.target = new THREE.WebGLRenderTarget(w, h, {
      type: THREE.HalfFloatType,
      // Phones: 2× MSAA. At their pixel density it looks the same as 4× (compared on-device) and
      // saves about a tenth of the frame's GPU time.
      samples: quality === 'low' ? 0 : isPhone() ? 2 : 4,
      depthTexture: new THREE.DepthTexture(w, h, THREE.UnsignedIntType),
    });
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        tDiffuse: { value: this.target.texture },
        tDepth: { value: this.target.depthTexture },
        texel: { value: new THREE.Vector2(1 / w, 1 / h) },
        cameraNear: { value: camera.near },
        cameraFar: { value: camera.far },
        ink: { value: new THREE.Color('#1c130d') },
        strength: { value: quality === 'low' ? 0 : 0.78 },
        width: { value: Math.max(1, Math.round(pr)) },
        vignette: { value: 0.38 },
        warmth: { value: 0.035 },
        sharpen: { value: this.sharpenBase },
      },
      vertexShader: FinalShader.vertexShader,
      fragmentShader: FinalShader.fragmentShader,
      depthTest: false,
      depthWrite: false,
    });
    this.quad = new FullScreenQuad(this.material);
  }

  getScale(): number {
    return this.scale;
  }

  /** Change the internal render scale (dynamic resolution); sharpening rises as it drops. */
  setScale(scale: number): void {
    this.scale = scale;
    this.material.uniforms.sharpen!.value = this.sharpenBase ? Math.min(1, this.sharpenBase + (1 - scale) * 0.9) : 0;
    const size = this.renderer.getSize(new THREE.Vector2());
    this.setSize(size.x, size.y);
  }

  setSize(width: number, height: number): void {
    const pr = this.renderer.getPixelRatio() * this.scale;
    const w = Math.max(1, Math.round(width * pr));
    const h = Math.max(1, Math.round(height * pr));
    this.target.setSize(w, h);
    this.material.uniforms.texel!.value.set(1 / w, 1 / h);
    this.material.uniforms.width!.value = Math.max(1, Math.round(pr));
  }

  render(_dt: number): void {
    const r = this.renderer;
    r.setRenderTarget(this.target);
    r.render(this.scene, this.camera);
    r.setRenderTarget(null);
    this.material.uniforms.cameraNear!.value = this.camera.near;
    this.material.uniforms.cameraFar!.value = this.camera.far;
    this.quad.render(r);
  }

  dispose(): void {
    this.target.dispose();
    this.material.dispose();
    this.quad.dispose();
  }
}
